import { useState } from "react";
import { observer } from "mobx-react-lite";
import { DeleteModal } from "../../../../components";
import { useStore } from "../../../../helpers";
import { useCreateFacet } from "../../tars-hooks";
import SourceTreeNode from "./SourceTreeNode";
import { applyDataRepoFacetTemplateFromSource } from "./applyDataRepoFacetTemplate";
import type { SourceFile, SourceNode } from "./sources-types";
import { FolderOpen } from "lucide-react";

interface SourcesTreeProps {
  notebookId: number | null;
  onAddSource: (parentId: string | null, parentLabel: string) => void;
  onEditSource: (node: SourceNode) => void;
  onDelete: (id: string, label: string) => void;
}

function EmptyState() {
  return (
    <div className="flex min-h-[220px] flex-col items-center justify-center px-6 py-14 text-center">
      <div className="relative mb-5">
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50 blur-sm" />
        <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-gray-100 bg-white shadow-sm">
          <FolderOpen
            size={32}
            strokeWidth={1.5}
            className="text-gray-300"
            aria-hidden
          />
        </div>
      </div>
      <p className="mb-1.5 text-[15px] font-semibold tracking-tight text-gray-800">
        No sources yet
      </p>
      <p className="max-w-[260px] text-[13px] leading-relaxed text-gray-500">
        Add folders, files, data repos, or links using <strong>Add Source</strong> to
        organize your notebook.
      </p>
    </div>
  );
}

const SourcesTree = observer(function SourcesTree({
  notebookId,
  onAddSource,
  onEditSource,
  onDelete,
}: SourcesTreeProps) {
  const { tarsStore, tarsAnalyticsStore } = useStore();
  const createFacet = useCreateFacet();
  const [replaceTemplateFile, setReplaceTemplateFile] =
    useState<SourceFile | null>(null);
  const rootNodes = tarsStore.getChildren(null);

  const selectedCanvasId = tarsStore.selectedCanvas?.id ?? 0;

  const handleApplyDataRepoTemplate = (fileNode: SourceFile) => {
    if (!notebookId) return;
    const result = applyDataRepoFacetTemplateFromSource(tarsAnalyticsStore, {
      notebookId,
      selectedCanvasId,
      fileNode,
      createFacetMutate: createFacet.mutate,
    });
    if (result === "needs_replace_confirm") {
      setReplaceTemplateFile(fileNode);
    }
  };

  const handleReplaceJsonConfirm = (_e: unknown, _id: string) => {
    if (!replaceTemplateFile || !notebookId) return;
    applyDataRepoFacetTemplateFromSource(tarsAnalyticsStore, {
      notebookId,
      selectedCanvasId,
      fileNode: replaceTemplateFile,
      createFacetMutate: createFacet.mutate,
      confirmReplace: true,
    });
    setReplaceTemplateFile(null);
  };

  return (
    <div className="prototype--viewerContainer flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
        {rootNodes.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="border-b border-gray-100 px-3 pb-2.5 pt-4">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                Sources
              </p>
            </div>
            <ul className="space-y-px px-2 py-2" aria-label="Source list">
              {rootNodes.map((node) => (
                <SourceTreeNode
                  key={node.id}
                  node={node}
                  depth={0}
                  notebookId={notebookId}
                  onAddSource={onAddSource}
                  onEditSource={onEditSource}
                  onDelete={onDelete}
                  onApplyDataRepoTemplate={handleApplyDataRepoTemplate}
                />
              ))}
            </ul>
          </>
        )}
      </div>

      <DeleteModal
        isOpen={!!replaceTemplateFile}
        id={replaceTemplateFile?.id}
        title="Replace JSON?"
        confirmLabel="Replace"
        desc="Do you need to replace the current JSON?"
        onDeleteClick={handleReplaceJsonConfirm}
        onCancelClick={() => setReplaceTemplateFile(null)}
      />
    </div>
  );
});

export default SourcesTree;
